import React, { useState } from "react";
import CustomDropdown from "../../common/CustomDropdown";

const StaffTendersMobileTabs = ({
  activeTab,
  onTabChange,
  onFilterClick,
  sortBy,
  handleSortChange,
  searchTerm,
  handleSearchChange,
  onSubTabChange,
  activeSubTab,
  activePositionFilter,
  handlePositionFilterChange,
  staffInClub,
  canViewPitches,
  isYouthStaff = false,
  // Second reason to drop the TP / TP Lite pair, independent of isYouthStaff:
  // the switch separates club tiers and only means something on All Listed
  // Requests. Same prop name and meaning as club/TenderTabs.
  hideTenderTabs = false,
  // Applied tab (self-applications) — only for staff/coaches WITHOUT a club;
  // in-club staff can't pitch themselves, so the tab does not exist for them.
  showAppliedTab = false,
  // Jobseekers (no club) can only apply to staff-category requests (BE
  // CreatePitchViewSet.perform_create: player tenders demand the Agent
  // group), so the "Players" category is not offered to them — same rule and
  // prop name as club/TenderTabs. In-club staff keep all four options.
  jobseekerCategories = false,
}) => {
  const showSourceTabs = !isYouthStaff && !hideTenderTabs;
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  // The Applied tab lists the user's own pitches — the browse controls
  // (search, filter, pro/youth sort, TP/TP Lite, category dropdown) all act
  // on the TENDER query and would be dead switches there, so they hide.
  const isAppliedActive = activeTab === "applied";

  const handleSearchIconClick = () => {
    setIsSearchVisible(true);
  };

  const handleCloseSearch = () => {
    setIsSearchVisible(false);
  };

  if (isSearchVisible) {
    return (
      <div className="px-4 py-3 bg-white flex items-center gap-2 border-y border-[#0000001A]">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.5 9.5L7.325 7.325M8.5 4.5C8.5 6.70914 6.70914 8.5 4.5 8.5C2.29086 8.5 0.5 6.70914 0.5 4.5C0.5 2.29086 2.29086 0.5 4.5 0.5C6.70914 0.5 8.5 2.29086 8.5 4.5Z"
                stroke="#1A212F"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search by position or club name"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-8 pr-4 py-2 border border-[#E6E6E6] placeholder:text-sm placeholder:text-[#1E1E1E] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
            autoFocus
          />
        </div>
        <button
          onClick={handleCloseSearch}
          className="p-2"
          aria-label="Close search"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13M1 1L13 13"
              stroke="#1A212F"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Main tabs — ONE JOB PER ROW (Marin's layout ruling, mirrored from
          club/responsive/MobileTenderTabs.js:108-113): the tabs get the full
          width on their own row and scroll [scrollbar-width:none] as a
          fallback — no label ever wraps, truncates or slides out of reach
          behind an icon cluster. The old single-row layout clipped "Saved"
          for in-club staff at 375px even BEFORE the Applied tab existed.
          The tools (loupe, filter, Pro) move to their own right-aligned row
          below, browse tabs only. The inner mx-auto centres the tabs while
          they fit and collapses to zero the moment they overflow, so the
          strip keeps scrolling from its start. */}
      <div className="px-4 bg-white border-y border-[#0000001A]">
        <div className="flex overflow-x-auto [scrollbar-width:none]">
          <div className="flex items-center gap-4 mx-auto md:mx-0">
            {staffInClub && canViewPitches && (
              <div
                onClick={() => onTabChange("my-tenders")}
                className={`py-4 text-xs whitespace-nowrap flex-none ${
                  activeTab === "my-tenders"
                    ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                    : "text-[#1E1E1ECC]"
                }`}
              >
                My Requests
              </div>
            )}
            <div
              onClick={() => onTabChange("all-tenders")}
              className={`py-4 text-xs whitespace-nowrap flex-none ${
                activeTab === "all-tenders"
                  ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                  : "text-[#1E1E1ECC]"
              }`}
            >
              All listed Requests
            </div>
            <div
              onClick={() => onTabChange("saved")}
              className={`py-4 text-xs whitespace-nowrap flex-none ${
                activeTab === "saved"
                  ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                  : "text-[#1E1E1ECC]"
              }`}
            >
              Saved
            </div>
            {showAppliedTab && (
              <div
                onClick={() => onTabChange("applied")}
                className={`py-4 text-xs whitespace-nowrap flex-none ${
                  activeTab === "applied"
                    ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                    : "text-[#1E1E1ECC]"
                }`}
              >
                Applied
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tools row: loupe, filter, Pro — right-aligned, own row (club
          pattern). Dead on the Applied tab, so it hides there. */}
      {!isAppliedActive && (
        <div className="px-4 py-2 bg-white flex justify-end items-center border-b border-[#0000001A]">
          <div className="flex items-center gap-2 flex-none">
            <div
              className="p-3 bg-[#F7F7F7] rounded-lg"
              onClick={handleSearchIconClick}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.5 9.5L7.325 7.325M8.5 4.5C8.5 6.70914 6.70914 8.5 4.5 8.5C2.29086 8.5 0.5 6.70914 0.5 4.5C0.5 2.29086 2.29086 0.5 4.5 0.5C6.70914 0.5 8.5 2.29086 8.5 4.5Z"
                  stroke="#1A212F"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="p-3 bg-[#F7F7F7] rounded-lg" onClick={onFilterClick}>
              <svg
                width="12"
                height="10"
                viewBox="0 0 12 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 0.5H1L5 5.23V8.5L7 9.5V5.23L11 0.5Z"
                  stroke="#1A212F"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="text-xs">
              <CustomDropdown
                options={[
                  { value: "pro", label: "Pro" },
                  { value: "youth", label: "Youth" },
                ]}
                value={sortBy}
                onChange={handleSortChange}
                placeholder="Pro"
                className="flex items-center gap-2 p-3 bg-[#E9F3FF] text-[#2283FF] rounded-lg font-medium"
                disabled={isYouthStaff}
              />
            </div>
          </div>
        </div>
      )}

      {!isAppliedActive && (
      <div
        className={`px-4 bg-white flex items-center border-b border-[#0000001A] ${
          showSourceTabs ? "justify-between" : "justify-end"
        }`}
      >
        {showSourceTabs && (
          <div className="flex items-center gap-4">
            <div
              onClick={() => onSubTabChange("tp-tenders")}
              className={`py-4 text-xs ${
                activeSubTab === "tp-tenders"
                  ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                  : "text-[#1E1E1ECC]"
              }`}
            >
              TP Requests
            </div>
            <div
              onClick={() => onSubTabChange("tp-lite-tenders")}
              className={`py-4 text-xs ${
                activeSubTab === "tp-lite-tenders"
                  ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                  : "text-[#1E1E1ECC]"
              }`}
            >
              TP Lite Requests
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          <div className="text-xs">
            <CustomDropdown
              options={
                jobseekerCategories
                  ? [
                      { value: "coaches", label: "Coaches" },
                      { value: "staffs", label: "Staffs" },
                      { value: "others", label: "Others" },
                    ]
                  : [
                      { value: "players", label: "Players" },
                      { value: "coaches", label: "Coaches" },
                      { value: "staffs", label: "Staffs" },
                      { value: "others", label: "Others" },
                    ]
              }
              value={activePositionFilter}
              onChange={handlePositionFilterChange}
              placeholder={jobseekerCategories ? "Staffs" : "Players"}
              className="flex items-center gap-2 p-3 bg-[#E9F3FF] text-[#2283FF] rounded-lg "
            />
          </div>
        </div>
      </div>
      )}
    </>
  );
};

export default StaffTendersMobileTabs;